import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpOnSquareIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentPlusIcon,
  FolderPlusIcon,
  InformationCircleIcon,
  LinkIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  ScissorsIcon,
  Squares2X2Icon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { OFFICE_APPS, type OfficeAppId } from '@/lib/office/officeApps';

interface DriveToolbarProps {
  isTrashView: boolean;
  viewMode: 'grid' | 'list';
  sortBy: 'name' | 'size' | 'date';
  sortAscending: boolean;
  hasSelection: boolean;
  hasClipboard: boolean;
  showDetails: boolean;
  searchQuery: string;
  searching: boolean;
  onViewModeChange: (mode: 'grid' | 'list') => void;
  onSortByChange: (sortBy: 'name' | 'size' | 'date') => void;
  onSortDirectionToggle: () => void;
  onToggleDetails: () => void;
  onSearchChange: (query: string) => void;
  onAction: (action: string) => void;
  onNewOffice: (appId: OfficeAppId) => void;
}

interface ToolbarButton {
  action: string;
  label: string;
  Icon: typeof PencilIcon;
  enabled: boolean;
}

/**
 * The drive's action bar. Horizontal by design: it never wraps or overlaps —
 * the row scrolls when the pane narrows, while every control keeps its size.
 */
export function DriveToolbar({
  isTrashView,
  viewMode,
  sortBy,
  sortAscending,
  hasSelection,
  hasClipboard,
  showDetails,
  searchQuery,
  searching,
  onViewModeChange,
  onSortByChange,
  onSortDirectionToggle,
  onToggleDetails,
  onSearchChange,
  onAction,
  onNewOffice,
}: DriveToolbarProps) {
  const { t } = useTranslation();
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const OFFICE_NEW_LABELS: Record<OfficeAppId, string> = {
    yaz: t('drive.newDocument'),
    diz: t('drive.newSpreadsheet'),
    sun: t('drive.newPresentation'),
    baz: t('drive.newDatabase'),
  };

  const buttons: ToolbarButton[] = isTrashView
    ? [
        { action: 'restore', label: t('drive.restore'), Icon: ArrowPathIcon, enabled: hasSelection },
        { action: 'permanent', label: t('drive.permanentDelete'), Icon: TrashIcon, enabled: hasSelection },
        { action: 'emptyTrash', label: t('drive.emptyTrash'), Icon: TrashIcon, enabled: true },
      ]
    : [
        { action: 'cut', label: t('drive.cut'), Icon: ScissorsIcon, enabled: hasSelection },
        { action: 'copy', label: t('drive.copy'), Icon: ClipboardDocumentIcon, enabled: hasSelection },
        { action: 'paste', label: t('drive.paste'), Icon: ClipboardDocumentListIcon, enabled: hasClipboard },
        { action: 'download', label: t('drive.download'), Icon: ArrowDownTrayIcon, enabled: hasSelection },
        { action: 'share', label: t('drive.share'), Icon: LinkIcon, enabled: hasSelection },
        { action: 'versions', label: t('drive.versionsMenu'), Icon: ClockIcon, enabled: hasSelection },
        { action: 'rename', label: t('drive.rename'), Icon: PencilIcon, enabled: hasSelection },
        { action: 'delete', label: t('drive.delete'), Icon: TrashIcon, enabled: hasSelection },
      ];

  return (
    <div
      className="flex flex-nowrap items-center gap-1 overflow-x-auto rounded-lg border border-border-color bg-card p-1.5"
      data-tour-id="action-toolbar"
    >
      {!isTrashView && (
        <>
          <button
            type="button"
            title={t('drive.upload')}
            aria-label={t('drive.upload')}
            onClick={() => onAction('upload')}
            className="flex-shrink-0 rounded-md bg-accent p-2 text-background transition-opacity hover:opacity-90"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            title={t('drive.uploadFolder')}
            aria-label={t('drive.uploadFolder')}
            onClick={() => onAction('uploadFolder')}
            className="flex-shrink-0 rounded-md p-2 text-foreground transition-colors hover:bg-muted"
          >
            <ArrowUpOnSquareIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            title={t('drive.newFolder')}
            aria-label={t('drive.newFolder')}
            onClick={() => onAction('newFolder')}
            className="flex-shrink-0 rounded-md p-2 text-foreground transition-colors hover:bg-muted"
          >
            <FolderPlusIcon className="h-4 w-4" />
          </button>
          {/* The office tools: New starts a blank document in yaz/diz/sun/baz that saves
              back to the drive through its bridge. */}
          <div className="relative flex-shrink-0">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={newMenuOpen}
              title={t('drive.newOffice')}
              aria-label={t('drive.newOffice')}
              onClick={() => setNewMenuOpen((open) => !open)}
              className="flex-shrink-0 rounded-md p-2 text-foreground transition-colors hover:bg-muted"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
            {newMenuOpen && (
              <div
                role="menu"
                className="absolute start-0 top-full z-30 mt-1 w-max min-w-44 rounded-lg border border-border-color bg-card p-1 shadow-lg"
              >
                {OFFICE_APPS.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setNewMenuOpen(false);
                      onNewOffice(app.id);
                    }}
                    className="flex w-full items-center whitespace-nowrap rounded-md px-3 py-1.5 text-start text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    <DocumentPlusIcon className="me-2 h-4 w-4 flex-shrink-0 text-accent" />
                    {OFFICE_NEW_LABELS[app.id]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="mx-1 h-5 w-px flex-shrink-0 bg-border-color" aria-hidden="true" />
          {/* Drive-wide search: names only, answered by the vault's search door. */}
          <label className="relative flex min-w-0 flex-1 items-center sm:max-w-64">
            <MagnifyingGlassIcon className="pointer-events-none absolute start-2 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => onSearchChange(event.target.value)}
              className="w-full min-w-0 rounded-md border border-border-color bg-background py-1.5 pe-7 ps-8 text-sm text-foreground placeholder:text-muted-foreground"
              aria-label={t('drive.searchPlaceholder')}
            />
            {searching && (
              <span className="absolute end-2 h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-border-color border-t-accent" aria-hidden="true" />
            )}
            {!searching && searchQuery && (
              <button
                type="button"
                title={t('common.close')}
                onClick={() => onSearchChange('')}
                className="absolute end-1.5 flex-shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </label>
          <span className="mx-1 h-5 w-px flex-shrink-0 bg-border-color" aria-hidden="true" />
        </>
      )}

      {buttons.map(({ action, label, Icon, enabled }) => (
        <button
          key={action}
          type="button"
          title={label}
          aria-label={label}
          disabled={!enabled}
          onClick={() => onAction(action)}
          className={clsx(
            'flex-shrink-0 rounded-md p-2 transition-colors',
            enabled
              ? 'text-foreground hover:bg-muted'
              : 'cursor-not-allowed text-muted-foreground opacity-40',
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}

      <span className="mx-1 h-5 w-px flex-shrink-0 bg-border-color" aria-hidden="true" />

      <label className="flex flex-shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
        <span className="whitespace-nowrap">{t('drive.sortBy')}</span>
        <select
          value={sortBy}
          onChange={(event) => onSortByChange(event.target.value as 'name' | 'size' | 'date')}
          className="whitespace-nowrap rounded-md border border-border-color bg-card px-2 py-1 text-sm text-foreground"
        >
          <option value="name">{t('drive.sortName')}</option>
          <option value="size">{t('drive.sortSize')}</option>
          <option value="date">{t('drive.sortDate')}</option>
        </select>
        <button
          type="button"
          onClick={onSortDirectionToggle}
          title={sortAscending ? t('drive.sortAscending') : t('drive.sortDescending')}
          className="whitespace-nowrap rounded-md px-2 py-1 text-sm text-foreground transition-colors hover:bg-muted"
        >
          {sortAscending ? '↑' : '↓'}
        </button>
      </label>

      <span className="flex-1" aria-hidden="true" />

      <div className="flex flex-shrink-0 items-center gap-0.5">
        <button
          type="button"
          title={t('drive.details')}
          aria-label={t('drive.details')}
          aria-pressed={showDetails}
          onClick={onToggleDetails}
          className={clsx(
            'rounded-md p-2 transition-colors',
            showDetails ? 'bg-active text-foreground' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          <InformationCircleIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          title={t('drive.viewGrid')}
          aria-label={t('drive.viewGrid')}
          aria-pressed={viewMode === 'grid'}
          onClick={() => onViewModeChange('grid')}
          className={clsx(
            'rounded-md p-2 transition-colors',
            viewMode === 'grid' ? 'bg-active text-foreground' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          <Squares2X2Icon className="h-4 w-4" />
        </button>
        <button
          type="button"
          title={t('drive.viewList')}
          aria-label={t('drive.viewList')}
          aria-pressed={viewMode === 'list'}
          onClick={() => onViewModeChange('list')}
          className={clsx(
            'rounded-md p-2 transition-colors',
            viewMode === 'list' ? 'bg-active text-foreground' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          <ListBulletIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
